import React, { CSSProperties, useEffect, useState } from 'react';
import { Cell, Grid }                                from 'styled-css-grid';
import { Checkbox, Header, Icon, List, Segment }     from 'semantic-ui-react';
import _                                             from 'underscore';
import AhoCorasick                                   from 'ahocorasick';

import { SelectField, TextField }                                       from '@src/views/components/forms';
import { ENTITY_ICONS, SELECTFIELD_NONE_VALUE }                         from '@src/constants';
import { formatOption, runIfFunction }                                  from '@src/util';
import { TemplateDisplayContainer }                                     from '@src/views/pages/projects/operations/templates/TemplateDisplayContainer';
import { CorvalProjectTemplate, SemanticUIColor, SemanticUISize, Uuid } from '@src/types';
import { ClassValue }                                                   from 'classnames/types';
import { EmptyCollectionMessage }                                       from '@src/views/components/util/messaging/EmptyCollectionMessage';
import { UseIf }                                                        from '@src/views/components/display/EqualValues';
import { ItemWithIcon }                                                 from '@src/views/components/display/ItemWithIcon';
import { StyledContainer }                                              from '@src/views/containers';


interface TemplateListingWithSearchProps {
    items: Array<CorvalProjectTemplate | ( CorvalProjectTemplate & { tags: string[] } )>,
    selectable?: boolean,
    selectedItemId?: Uuid,
    containerClasses?: ClassValue,
    containerStyles?: CSSProperties,
    onListItemClicked: ( templateId: Uuid ) => void,
    showArchivedToggle?: boolean,
    hideArchived?: boolean,
}

const TemplateListingWithSearchDefaultProps = {
    items:              [],
    selectable:         false,
    selectedItemId:     null,
    containerClasses:   null,
    containerStyles:    {},
    onListItemClicked:  null,
    showArchivedToggle: false,
    hideArchived:       false,
} satisfies Partial<TemplateListingWithSearchProps>;

function TemplateListingWithSearch( _props: TemplateListingWithSearchProps ){

    const props = { ...TemplateListingWithSearchDefaultProps, ..._props };

    const [ templateListing, setTemplateListing ]                 = useState<CorvalProjectTemplate[]>( [] ),
          [ templateCategories, setTemplateCategories ]           = useState<string[]>( [] ),
          [ templateSearchFilter, setTemplateSearchFilter ]       = useState( null ),
          [ archivedTemplatesHidden, setArchivedTemplatesHidden ] = useState( props.hideArchived ?? false ),
          [ templateCategoryFilter, setTemplateCategoryFilter ]   = useState( SELECTFIELD_NONE_VALUE );

    useEffect( () => {

        const categories = _.chain( props.items )
                            .pluck( 'category' )
                            .uniq()
                            .sort()
                            .value();

        setTemplateListing( props.items );
        setTemplateCategories( categories );

        if( templateCategoryFilter !== SELECTFIELD_NONE_VALUE && !categories.includes( templateCategoryFilter ) ){
            setTemplateCategoryFilter( SELECTFIELD_NONE_VALUE );
        }

    }, [ props.items ] );

    useEffect( () => {}, [ templateSearchFilter ] );

    useEffect( () => {}, [ archivedTemplatesHidden ] );

    const handleTemplateClicked = ( value: string ) => {

        runIfFunction( props.onListItemClicked, value );

    };
    const handleSearchFilterChanged = ( value: string ) => {

        setTemplateSearchFilter( value );

    };

    const getTemplatesByCategory = ( templateSet ) => {

        return _.chain( templateSet )
                .groupBy( 'category' )
                .pairs()
                .sortBy( ( [ category, templates ] ) => category )
                .value();
    };

    const getFilteredTemplates = () => {

        return templateListing
            .filter( template => {

                if( template?.isArchived === false ) return true;

                return ( props.showArchivedToggle && ( template.isArchived && !archivedTemplatesHidden ) );

            } )
            .filter( template => {

                const noCategoryFilter    = ( [ SELECTFIELD_NONE_VALUE, null ].includes( templateCategoryFilter ) ),
                      templateHasCategory = ( template.category === templateCategoryFilter );

                let hasSearchFilter     = !_.isEmpty( templateSearchFilter ),
                    matchesSearchFilter = true;

                if( hasSearchFilter ){

                    const nameTokens        = _.compact( template.name.trim().toLowerCase().split( ' ' ) ),
                          descriptionTokens = _.compact( template.description.trim().toLowerCase().split( ' ' ) ),
                          tagTokens         = _.compact( ( template.tags ?? [] ).map( ( tag ) => {
                              const t = _.isObject( tag ) ? tag.tag : tag;
                              return t.trim().toLowerCase();
                          } ) );

                    const templateFilteredSearchAlgo   = new AhoCorasick( [ templateSearchFilter.trim().toLowerCase() ] ),
                          templateFilteredSearchResult = templateFilteredSearchAlgo.search( _.union( nameTokens, descriptionTokens, tagTokens ).join( ' ' ) );

                    matchesSearchFilter = !_.isEmpty( templateFilteredSearchResult );
                }

                return ( ( noCategoryFilter || templateHasCategory ) && ( !hasSearchFilter || matchesSearchFilter ) );
            } );
    };

    return (

        <UseIf x={ _.isEmpty( templateListing ) } eq={ false } else={ () => (

            <EmptyCollectionMessage
                header={ 'No templates available' }
                content={ 'It appears that there are no templates to manage, or that you do not have access to manage the available templates on this system. Check with an authoring admin to ensure proper template management access, or try creating your own template from an existing project.' }
            />

        ) }>

            <Grid rows={ `auto 1fr` } columns={ `1fr` } style={ { height: '100%' } }>

                <Cell>

                    <Grid columns={ `1fr minmax(auto,40%)` }>

                        { props.showArchivedToggle && (

                            <Cell left={ 2 } className={ 'text-right' }>

                                <Checkbox fitted={ true }
                                          id={ 'archivedTemplatesHidden' }
                                          checked={ archivedTemplatesHidden }
                                          label={ `Hide Archived` }
                                          onClick={ ( e, data ) => {
                                              setArchivedTemplatesHidden( data.checked );
                                          } }
                                />

                            </Cell>
                        ) }

                        <Cell>

                            <TextField
                                fluid
                                key={ templateSearchFilter }
                                name={ 'searchFilter' }
                                icon={ 'search' }
                                iconPosition={ 'left' }
                                placeholder={ 'Search Templates...' }
                                debounce={ true }
                                debounceValue={ templateSearchFilter }
                                value={ templateSearchFilter }
                                onDebounceChange={ handleSearchFilterChanged }
                                action={ _.isEmpty( templateSearchFilter ) ? null : {
                                    icon:    'delete',
                                    basic:   true,
                                    size:    'mini',
                                    onClick: () => handleSearchFilterChanged( null ),
                                } }
                                style={ {
                                    clearable: true,
                                } }
                            />

                        </Cell>
                        <Cell>

                            <SelectField
                                fluid
                                name={ 'categoryFilter' }
                                onChange={ setTemplateCategoryFilter }
                                value={ templateCategoryFilter }
                                preventFirstSelection={ false }
                                options={ [
                                    formatOption( SELECTFIELD_NONE_VALUE, 'All Categories' ),
                                    ...templateCategories.map( category => formatOption( category, category, ) )
                                ] }
                            />

                        </Cell>

                    </Grid>

                </Cell>
                <Cell className={ 'position--relative overflow--hidden' }>

                    <Segment secondary className={ props.containerClasses } style={ props.containerStyles }>

                        { templateCategories.map( ( category, index ) => {

                            const templates = _.chain( getFilteredTemplates() )
                                               .filter( t => t.category === category )
                                               .sortBy( 'name' )
                                               .value();

                            if( _.isEmpty( templates ) ) return null;

                            return (
                                <>
                                    { ( templateCategoryFilter === SELECTFIELD_NONE_VALUE ) && (
                                        <StyledContainer className={ 'padding-md-left padding-md-top' }>
                                            <ItemWithIcon iconComponent={ <Icon fixed size={ SemanticUISize.LARGE } color={ SemanticUIColor.PURPLE } name={ ENTITY_ICONS.PROJECTS.TEMPLATES.ENTITY }/> }>
                                                <Header className={ 'no-margin' } color={ SemanticUIColor.PURPLE } as={ 'h5' }>{ category }</Header>
                                            </ItemWithIcon>
                                        </StyledContainer>
                                    ) }
                                    <List relaxed>
                                        { templates.map( template => {

                                            return (
                                                <List.Item key={ template.id }>
                                                    <TemplateDisplayContainer
                                                        key={ template.id }
                                                        id={ template.id }
                                                        name={ template.name }
                                                        icon={ template.icon as any }
                                                        description={ template.description }
                                                        isArchived={ template.isArchived }
                                                        selectable={ props.selectable }
                                                        selected={ template.id === props.selectedItemId }
                                                        onClick={ handleTemplateClicked }
                                                    />
                                                </List.Item>
                                            );
                                        } )
                                        }
                                    </List>

                                </>
                            );

                        } ) }

                    </Segment>

                </Cell>

            </Grid>

        </UseIf>
    );
}

export {
    TemplateListingWithSearch,
    TemplateListingWithSearchDefaultProps,
    type TemplateListingWithSearchProps,
};
